import { useState } from 'react';
import type { AlertColor, SxProps } from '@mui/material';
import AlertDialog from '@/components/AlertDialog';

interface ShowAlertProps {
  severity: AlertColor; // 'error' | 'warning' | 'info' | 'success'
  title?: string;
  message: string;
  duration?: number; // duration in milliseconds before auto-hiding
  showCloseButton?: boolean;
  onClose?: () => void;
  action?: React.ReactNode; // optional action component
  sx?: SxProps; // optional sx props
}

interface AlertState extends ShowAlertProps {
  visible: boolean;
  id: string;
}

export function useAlertDialog() {
  const [alerts, setAlerts] = useState<AlertState[]>([]);

  const showAlert = ({
    severity,
    title,
    message,
    duration = 6000,
    showCloseButton = true,
    onClose,
    action,
    sx
  }: ShowAlertProps) => {
    const id = Date.now().toString();
    const newAlert: AlertState = {
      id,
      visible: true,
      severity,
      title,
      message,
      duration,
      showCloseButton,
      onClose,
      action,
      sx
    };

    setAlerts([newAlert]);

    // Auto close after duration
    if (duration !== null && duration !== undefined && duration > 0) {
      setTimeout(() => {
        closeAlert(id);
      }, duration);
    }

    return id;
  };

  const closeAlert = (id: string) => {
    setAlerts((prevAlerts) => {
      const alertToClose = prevAlerts.find(alert => alert.id === id);
      if (alertToClose) {
        alertToClose.onClose?.();
      }
      return prevAlerts.filter(alert => alert.id !== id);
    });
  };

  // Component to render alerts at a specific location
  // Limited to show only one alert at a time (the most recent one)
  const AlertContainer = () => {
    if (alerts.length === 0) return null;
    
    // Get only the most recent alert
    const latestAlert = alerts[alerts.length - 1];
    
    return (
      <div className="my-4">
        <AlertDialog
          key={latestAlert.id}
          id={latestAlert.id}
          severity={latestAlert.severity}
          title={latestAlert.title}
          message={latestAlert.message}
          closeAlert={closeAlert}
          action={latestAlert.action}
          sx={latestAlert.sx}
          showCloseButton={latestAlert.showCloseButton}
        />
      </div>
    );
  };

  return { showAlert, closeAlert, AlertContainer };
}
